import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Badge,
  Button,
  Card,
  Input,
  LoadingSpinner,
  Modal,
  ModalFooter,
  Select,
} from '@/components/common';
import { getErrorMessage, getFieldErrors } from '@/services/api';
import { geographyService } from '@/services/geography';
import {
  insurerProductsService,
  riskyChanges,
  STATUS_BADGE,
  STATUS_LABELS,
  type ProductDetail,
  type ProductRider,
  type ProductServiceArea,
  type RateChartSummary,
} from '@/services/insurerProducts';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import { ProductHistory } from './ProductHistory';
import { ProductStatusDialog } from './ProductStatusDialog';

/**
 * #398: the insurer's product form (create and edit), one full page in six tabs.
 *
 * No approval step: Save is live for brokers at once. Risky changes on an
 * active product (premiums, sum insured, ages, UIN, location rules, wording)
 * show an old -> new confirmation first. A stale save (someone else changed
 * the product) comes back 409 and offers a reload instead of overwriting.
 */

const TABS = [
  { key: 'basics', label: 'Basics' },
  { key: 'riders', label: 'Plans and riders' },
  { key: 'premium', label: 'Premium and rates' },
  { key: 'eligibility', label: 'Eligibility' },
  { key: 'documents', label: 'Underwriting and documents' },
  { key: 'settings', label: 'Settings' },
] as const;
type TabKey = (typeof TABS)[number]['key'] | 'history';

const CATEGORY_OPTIONS = [
  { value: '', label: 'Choose a category' },
  { value: 'LIFE', label: 'Life' },
  { value: 'HEALTH', label: 'Health' },
  { value: 'MOTOR', label: 'Motor' },
  { value: 'PROPERTY', label: 'Property' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'OTHER', label: 'Other' },
];

const FREQUENCY_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'one_time', label: 'One time' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half yearly' },
  { value: 'yearly', label: 'Yearly' },
];

const TEXT_FIELDS = [
  'name',
  'code',
  'category',
  'product',
  'uin',
  'description',
  'short_description',
  'seo_meta_title',
  'seo_meta_description',
  'seo_slug',
  'min_premium',
  'max_premium',
  'premium_frequency',
  'sum_insured_min',
  'sum_insured_max',
  'min_age',
  'max_age',
  'max_renewal_age',
  'eligibility_notes',
  'waiting_period',
  'exclusions',
  'wording_version',
  'wording_effective_date',
  'underwriting_guidelines',
  'product_page_url',
  'claim_process_url',
  'terms_url',
] as const;
type TextField = (typeof TEXT_FIELDS)[number];
const FILE_FIELDS = [
  'brochure_pdf',
  'policy_wording_pdf',
  'claim_form_pdf',
  'sample_policy_pdf',
] as const;
type FileField = (typeof FILE_FIELDS)[number];

type FormState = Record<TextField, string> & { is_visible_to_all: boolean; tags: string };
/** A chosen File, '' to remove the stored one, or undefined for "unchanged". */
type FileChanges = Partial<Record<FileField, File | ''>>;

const EMPTY_FORM: FormState = {
  ...(Object.fromEntries(TEXT_FIELDS.map((f) => [f, ''])) as Record<TextField, string>),
  category: 'HEALTH',
  is_visible_to_all: true,
  tags: '',
};

const EMPTY_RIDER: ProductRider = {
  name: '',
  code: '',
  rider_type: 'cover',
  irdai_rider_uin: '',
  description: '',
  percentage_of_premium: null,
  fixed_premium: null,
  sum_insured: null,
  is_mandatory: false,
  is_active: true,
};

const FIELD_CLASS =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400';
const LABEL_CLASS = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300';
const HELP_CLASS = 'mt-1 text-xs text-gray-500 dark:text-gray-400';
const ERROR_CLASS = 'mt-1 text-sm text-red-600 dark:text-red-400';
const TH_CLASS =
  'px-2 py-2 text-left text-xs font-semibold uppercase text-gray-600 dark:text-gray-300';

function fromProduct(p: ProductDetail): FormState {
  const form = { ...EMPTY_FORM };
  for (const field of TEXT_FIELDS) {
    const value = (p as unknown as Record<string, unknown>)[field];
    form[field] = value === null || value === undefined ? '' : String(value);
  }
  form.is_visible_to_all = p.is_visible_to_all;
  form.tags = (p.tags ?? []).join(', ');
  return form;
}

function fileName(path: string | null | undefined): string {
  if (!path) return '';
  return decodeURIComponent(path.split('?')[0].split('/').pop() || '');
}

function TextArea({
  id,
  label,
  value,
  onChange,
  rows = 3,
  help,
  error,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  help?: string;
  error?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={FIELD_CLASS}
      />
      {help && !error && <p className={HELP_CLASS}>{help}</p>}
      {error && <p className={ERROR_CLASS}>{error}</p>}
    </div>
  );
}

export function ProductFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useInsurerPermissions();
  const canEdit = can(isEdit ? 'products.edit' : 'products.create');
  const canChangeStatus = can('products.edit');

  const [tab, setTab] = useState<TabKey>('basics');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [riders, setRiders] = useState<ProductRider[]>([]);
  const [areas, setAreas] = useState<ProductServiceArea[]>([]);
  const [files, setFiles] = useState<FileChanges>({});
  const [pinPaste, setPinPaste] = useState('');
  const [pinRule, setPinRule] = useState<'allow' | 'exclude'>('allow');
  const [stateToAdd, setStateToAdd] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [stale, setStale] = useState(false);
  const [confirmRows, setConfirmRows] = useState<
    { field: string; label: string; from: string; to: string }[] | null
  >(null);
  const [statusTarget, setStatusTarget] = useState<ProductDetail['status'] | null>(null);

  const productQuery = useQuery({
    queryKey: ['insurer-product', id],
    queryFn: () => insurerProductsService.get(id as string),
    enabled: isEdit,
  });
  const product = productQuery.data;

  const { data: catalogue = [] } = useQuery({
    queryKey: ['insurer-catalogue-products'],
    queryFn: insurerProductsService.catalogue,
  });
  const { data: states = [] } = useQuery({
    queryKey: ['geography-state-lookup'],
    queryFn: () => geographyService.getStateLookup(),
    staleTime: 10 * 60_000,
  });
  const { data: rateCharts = [] } = useQuery<RateChartSummary[]>({
    queryKey: ['insurer-product-rate-charts', id],
    queryFn: () => insurerProductsService.rateCharts(id as string),
    enabled: isEdit,
  });

  const loadFrom = (p: ProductDetail) => {
    setForm(fromProduct(p));
    setRiders(p.riders ?? []);
    setAreas(p.service_areas ?? []);
    setFiles({});
    setErrors({});
    setStale(false);
  };

  useEffect(() => {
    if (product) loadFrom(product);
  }, [product]);

  const original = useMemo(
    () =>
      product
        ? {
            ...fromProduct(product),
            riders: JSON.stringify(product.riders ?? []),
            service_areas: JSON.stringify(product.service_areas ?? []),
          }
        : { ...EMPTY_FORM, riders: '[]', service_areas: '[]' },
    [product]
  );
  const current = { ...form, riders: JSON.stringify(riders), service_areas: JSON.stringify(areas) };
  const dirty =
    !isEdit ||
    Object.keys(files).length > 0 ||
    (Object.keys(current) as (keyof typeof current)[]).some((k) => current[k] !== original[k]);

  const set = (field: keyof FormState, value: string | boolean) =>
    setForm((f) => ({ ...f, [field]: value }));

  const buildBody = (): FormData => {
    const body = new FormData();
    for (const field of TEXT_FIELDS) {
      // A new product leaves an empty optional field out (the API default is
      // "none"); an edit still sends "" so a stored value can be cleared.
      if (!isEdit && form[field] === '') continue;
      if (!isEdit || form[field] !== original[field]) body.append(field, form[field]);
    }
    if (!isEdit || form.is_visible_to_all !== original.is_visible_to_all) {
      body.append('is_visible_to_all', String(form.is_visible_to_all));
    }
    if (!isEdit || form.tags !== original.tags) {
      const tags = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      body.append('tags', JSON.stringify(tags));
    }
    if (!isEdit || current.riders !== original.riders)
      body.append('riders', JSON.stringify(riders));
    if (!isEdit || current.service_areas !== original.service_areas) {
      body.append(
        'service_areas',
        JSON.stringify(
          areas.map(({ rule_type, state, pincode }) => ({ rule_type, state, pincode }))
        )
      );
    }
    for (const field of FILE_FIELDS) {
      const change = files[field];
      if (change !== undefined) body.append(field, change);
    }
    if (isEdit && product) body.append('row_version', String(product.row_version));
    return body;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = buildBody();
      return isEdit
        ? insurerProductsService.update(id as string, body)
        : insurerProductsService.create(body);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['insurance-policies'] });
      queryClient.invalidateQueries({ queryKey: ['insurer-product-versions', saved.id] });
      setConfirmRows(null);
      if (isEdit) {
        queryClient.setQueryData(['insurer-product', id], { ...product, ...saved });
        queryClient.invalidateQueries({ queryKey: ['insurer-product', id] });
        toast.success('Saved. The change is live for brokers.');
      } else {
        toast.success('Product created as a draft.');
        navigate(`/insurance/policies/${saved.id}/edit`, { replace: true });
      }
    },
    onError: (err: unknown) => {
      setConfirmRows(null);
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        setStale(true);
        return;
      }
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    },
  });

  const onSave = () => {
    if (!form.name.trim() || !form.code.trim()) {
      setErrors({
        name: form.name.trim() ? '' : 'Name is required.',
        code: form.code.trim() ? '' : 'Code is required.',
      });
      setTab('basics');
      return;
    }
    if (isEdit && product?.status === 'active') {
      const rows = riskyChanges(
        {
          ...original,
          service_areas: original.service_areas,
          policy_wording_pdf: product.policy_wording_pdf,
        },
        {
          ...form,
          service_areas: current.service_areas,
          ...(files.policy_wording_pdf !== undefined
            ? { policy_wording_pdf: files.policy_wording_pdf }
            : {}),
        }
      );
      if (rows.length > 0) {
        setConfirmRows(rows);
        return;
      }
    }
    saveMutation.mutate();
  };

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      label,
      effective,
    }: {
      file: File;
      label: string;
      effective: string;
    }) => {
      const body = new FormData();
      body.append('file', file);
      body.append('label', label);
      if (effective) body.append('effective_from', effective);
      return insurerProductsService.uploadRateChart(id as string, body);
    },
    onSuccess: (chart) => {
      queryClient.invalidateQueries({ queryKey: ['insurer-product-rate-charts', id] });
      queryClient.invalidateQueries({ queryKey: ['insurer-product', id] });
      queryClient.invalidateQueries({ queryKey: ['insurer-product-versions', id] });
      toast.success(`Rate chart v${chart.chart_version} is live (${chart.row_count} rows).`);
      for (const warning of chart.warnings ?? []) toast(warning);
    },
    onError: (err: unknown) => {
      const fields = getFieldErrors(err);
      setErrors((e) => ({ ...e, rate_chart: fields.file || getErrorMessage(err) }));
    },
  });
  const [chartFile, setChartFile] = useState<File | null>(null);
  const [chartLabel, setChartLabel] = useState('');
  const [chartDate, setChartDate] = useState('');

  const addPins = () => {
    const pins = pinPaste
      .split(/[\s,;]+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .slice(0, 500);
    const have = new Set(areas.map((a) => `${a.rule_type}:${a.pincode}`));
    const next = pins
      .filter((pin) => !have.has(`${pinRule}:${pin}`))
      .map((pin) => ({ rule_type: pinRule, state: null, pincode: pin }) as ProductServiceArea);
    setAreas([...areas, ...next]);
    setPinPaste('');
  };

  const addState = () => {
    if (!stateToAdd) return;
    const found = states.find((s) => s.id === stateToAdd);
    if (!areas.some((a) => a.state === stateToAdd && a.rule_type === pinRule)) {
      setAreas([
        ...areas,
        { rule_type: pinRule, state: stateToAdd, state_name: found?.name ?? '', pincode: '' },
      ]);
    }
    setStateToAdd('');
  };

  if (isEdit && productQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  const readOnly = !canEdit;
  const err = (field: string) => errors[field] || undefined;
  const statusActions: {
    label: string;
    target: ProductDetail['status'];
    variant: 'primary' | 'outline' | 'danger';
  }[] = [];
  if (product && canChangeStatus) {
    if (product.status === 'draft' || product.status === 'inactive')
      statusActions.push({ label: 'Activate', target: 'active', variant: 'primary' });
    if (product.status === 'active')
      statusActions.push({ label: 'Deactivate', target: 'inactive', variant: 'outline' });
    if (product.status !== 'discontinued')
      statusActions.push({ label: 'Discontinue', target: 'discontinued', variant: 'danger' });
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? `Edit ${product?.name ?? 'product'}` : 'Add Product'}
        subtitle={
          isEdit
            ? 'Changes go live for your brokers as soon as you save. Every change is kept in History and can be reverted.'
            : 'New products start as a draft. Activate them when they are complete.'
        }
        breadcrumbs={[
          { label: 'Insurance' },
          { label: 'Products', href: '/insurance/policies' },
          { label: isEdit ? 'Edit' : 'Add' },
        ]}
        actions={
          product && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={STATUS_BADGE[product.status]}>{STATUS_LABELS[product.status]}</Badge>
              {statusActions.map((a) => (
                <Button
                  key={a.target}
                  size="sm"
                  variant={a.variant}
                  onClick={() => setStatusTarget(a.target)}
                >
                  {a.label}
                </Button>
              ))}
            </div>
          )
        }
      />

      {stale && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <span>
            Someone else changed this product. Reload it to see their change, then make yours again.
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setTab('history')}>
              Compare in History
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                const fresh = await productQuery.refetch();
                if (fresh.data) loadFrom(fresh.data);
              }}
            >
              Reload
            </Button>
          </div>
        </div>
      )}

      {product?.status === 'active' && (
        <p className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
          This product is on sale. Saving updates what brokers see immediately.
        </p>
      )}

      <div role="tablist" aria-label="Product sections" className="mb-4 flex flex-wrap gap-2">
        {[...TABS, ...(isEdit ? [{ key: 'history' as const, label: 'History' }] : [])].map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-full px-3 py-1 text-sm ${
              tab === t.key
                ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        <fieldset disabled={readOnly || saveMutation.isPending} className="space-y-4">
          {tab === 'basics' && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                name="name"
                label="Product name"
                required
                value={form.name}
                error={err('name')}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. Health Shield Gold"
              />
              <Input
                name="code"
                label="Product code"
                required
                value={form.code}
                error={err('code')}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                placeholder="e.g. HSG-001"
                helperText="Capital letters, digits, - and _ only."
              />
              <Select
                name="category"
                label="Category"
                options={CATEGORY_OPTIONS}
                value={form.category}
                error={err('category')}
                onChange={(e) => set('category', e.target.value)}
              />
              <Select
                name="product"
                label="Catalogue product"
                options={[
                  { value: '', label: 'Not mapped yet' },
                  ...catalogue.map((c) => ({
                    value: c.id,
                    label: c.insurance_type_name ? `${c.name} (${c.insurance_type_name})` : c.name,
                  })),
                ]}
                value={form.product}
                error={err('product')}
                onChange={(e) => set('product', e.target.value)}
                helperText="Needed to activate. Missing a catalogue product? Ask your country admin."
              />
              <Input
                name="uin"
                label="IRDAI UIN"
                value={form.uin}
                error={err('uin')}
                onChange={(e) => set('uin', e.target.value.toUpperCase())}
                helperText="Needed to activate."
              />
              <Input
                name="short_description"
                label="Short description"
                value={form.short_description}
                error={err('short_description')}
                onChange={(e) => set('short_description', e.target.value)}
              />
              <div className="md:col-span-2">
                <TextArea
                  id="description"
                  label="Description"
                  value={form.description}
                  error={err('description')}
                  onChange={(v) => set('description', v)}
                />
              </div>
              <Input
                name="seo_meta_title"
                label="SEO meta title"
                value={form.seo_meta_title}
                onChange={(e) => set('seo_meta_title', e.target.value)}
              />
              <Input
                name="seo_slug"
                label="URL slug"
                value={form.seo_slug}
                error={err('seo_slug')}
                onChange={(e) =>
                  set(
                    'seo_slug',
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]+/g, '-')
                      .replace(/^-+/, '')
                  )
                }
              />
              <div className="md:col-span-2">
                <TextArea
                  id="seo_meta_description"
                  label="SEO meta description"
                  rows={2}
                  value={form.seo_meta_description}
                  onChange={(v) => set('seo_meta_description', v)}
                />
              </div>
            </div>
          )}

          {tab === 'riders' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Add-ons and riders brokers can offer with this product. Give a percentage of premium
                or a fixed premium.
              </p>
              {err('riders') && <p className={ERROR_CLASS}>{err('riders')}</p>}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr>
                      <th className={TH_CLASS}>Name</th>
                      <th className={TH_CLASS}>Code</th>
                      <th className={TH_CLASS}>Type</th>
                      <th className={TH_CLASS}>% of premium</th>
                      <th className={TH_CLASS}>Fixed premium</th>
                      <th className={TH_CLASS}>Mandatory</th>
                      <th className={TH_CLASS} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {riders.map((r, i) => {
                      const update = (patch: Partial<ProductRider>) =>
                        setRiders(riders.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                      return (
                        <tr key={i}>
                          <td className="px-2 py-1">
                            <input
                              aria-label={`Rider ${i + 1} name`}
                              className={FIELD_CLASS}
                              value={r.name}
                              onChange={(e) => update({ name: e.target.value })}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              aria-label={`Rider ${i + 1} code`}
                              className={FIELD_CLASS}
                              value={r.code}
                              onChange={(e) => update({ code: e.target.value.toUpperCase() })}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <select
                              aria-label={`Rider ${i + 1} type`}
                              className={FIELD_CLASS}
                              value={r.rider_type}
                              onChange={(e) =>
                                update({ rider_type: e.target.value as ProductRider['rider_type'] })
                              }
                            >
                              <option value="cover">Cover</option>
                              <option value="benefit">Benefit</option>
                              <option value="service">Service</option>
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <input
                              aria-label={`Rider ${i + 1} percentage`}
                              inputMode="decimal"
                              className={FIELD_CLASS}
                              value={r.percentage_of_premium ?? ''}
                              onChange={(e) =>
                                update({ percentage_of_premium: e.target.value || null })
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              aria-label={`Rider ${i + 1} fixed premium`}
                              inputMode="decimal"
                              className={FIELD_CLASS}
                              value={r.fixed_premium ?? ''}
                              onChange={(e) => update({ fixed_premium: e.target.value || null })}
                            />
                          </td>
                          <td className="px-2 py-1 text-center">
                            <input
                              type="checkbox"
                              aria-label={`Rider ${i + 1} mandatory`}
                              checked={r.is_mandatory}
                              onChange={(e) => update({ is_mandatory: e.target.checked })}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                            />
                          </td>
                          <td className="px-2 py-1 text-right">
                            <button
                              type="button"
                              aria-label={`Remove rider ${i + 1}`}
                              onClick={() => setRiders(riders.filter((_, j) => j !== i))}
                              className="rounded p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<PlusIcon className="h-4 w-4" />}
                onClick={() => setRiders([...riders, { ...EMPTY_RIDER }])}
              >
                Add rider
              </Button>
            </div>
          )}

          {tab === 'premium' && (
            <div className="space-y-4">
              <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                Rates are shown to brokers as soon as you save. Quote premiums come from your
                integration, not from these charts.
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Input
                  name="min_premium"
                  label="Minimum premium"
                  inputMode="decimal"
                  value={form.min_premium}
                  error={err('min_premium')}
                  onChange={(e) => set('min_premium', e.target.value)}
                />
                <Input
                  name="max_premium"
                  label="Maximum premium"
                  inputMode="decimal"
                  value={form.max_premium}
                  error={err('max_premium')}
                  onChange={(e) => set('max_premium', e.target.value)}
                />
                <Select
                  name="premium_frequency"
                  label="Premium frequency"
                  options={FREQUENCY_OPTIONS}
                  value={form.premium_frequency}
                  onChange={(e) => set('premium_frequency', e.target.value)}
                />
                <Input
                  name="sum_insured_min"
                  label="Minimum sum insured"
                  inputMode="decimal"
                  value={form.sum_insured_min}
                  error={err('sum_insured_min')}
                  onChange={(e) => set('sum_insured_min', e.target.value)}
                />
                <Input
                  name="sum_insured_max"
                  label="Maximum sum insured"
                  inputMode="decimal"
                  value={form.sum_insured_max}
                  error={err('sum_insured_max')}
                  onChange={(e) => set('sum_insured_max', e.target.value)}
                />
              </div>
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Rate charts
                </h3>
                {!isEdit ? (
                  <p className={HELP_CLASS}>Save the product first, then upload a rate chart.</p>
                ) : (
                  <div className="mt-2 space-y-3">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <div>
                        <label htmlFor="rate-file" className={LABEL_CLASS}>
                          CSV file
                        </label>
                        <input
                          id="rate-file"
                          type="file"
                          accept=".csv,text/csv"
                          onChange={(e) => setChartFile(e.target.files?.[0] ?? null)}
                          className="block w-full text-sm text-gray-700 dark:text-gray-300"
                        />
                      </div>
                      <Input
                        label="Label"
                        name="rate_label"
                        value={chartLabel}
                        onChange={(e) => setChartLabel(e.target.value)}
                      />
                      <Input
                        label="Effective from"
                        name="rate_effective_from"
                        type="date"
                        value={chartDate}
                        onChange={(e) => setChartDate(e.target.value)}
                      />
                      <div className="flex items-end gap-2">
                        <Button
                          size="sm"
                          disabled={!chartFile}
                          isLoading={uploadMutation.isPending}
                          onClick={() => {
                            if (!chartFile) return;
                            setErrors((e) => ({ ...e, rate_chart: '' }));
                            uploadMutation.mutate({
                              file: chartFile,
                              label: chartLabel,
                              effective: chartDate,
                            });
                          }}
                        >
                          Upload
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => insurerProductsService.downloadTemplate(id as string)}
                        >
                          Template
                        </Button>
                      </div>
                    </div>
                    {err('rate_chart') && <p className={ERROR_CLASS}>{err('rate_chart')}</p>}
                    {rateCharts.length > 0 && (
                      <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/40">
                          <tr>
                            <th className={TH_CLASS}>Version</th>
                            <th className={TH_CLASS}>Label</th>
                            <th className={TH_CLASS}>Effective</th>
                            <th className={TH_CLASS}>Rows</th>
                            <th className={TH_CLASS}>Uploaded by</th>
                            <th className={TH_CLASS} />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {rateCharts.map((c) => (
                            <tr key={c.id}>
                              <td className="px-2 py-1 text-gray-900 dark:text-gray-100">
                                v{c.chart_version}{' '}
                                {c.is_current && (
                                  <Badge size="sm" variant="success">
                                    Current
                                  </Badge>
                                )}
                              </td>
                              <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                                {c.label || '-'}
                              </td>
                              <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                                {c.effective_from}
                              </td>
                              <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                                {c.row_count}
                              </td>
                              <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                                {c.uploaded_by_label || '-'}
                              </td>
                              <td className="px-2 py-1 text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    insurerProductsService.downloadRateChart(
                                      id as string,
                                      c.id,
                                      `${form.code}-rates-v${c.chart_version}.csv`
                                    )
                                  }
                                >
                                  Download
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'eligibility' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Input
                  name="min_age"
                  label="Minimum entry age"
                  inputMode="numeric"
                  value={form.min_age}
                  error={err('min_age')}
                  onChange={(e) => set('min_age', e.target.value)}
                />
                <Input
                  name="max_age"
                  label="Maximum entry age"
                  inputMode="numeric"
                  value={form.max_age}
                  error={err('max_age')}
                  onChange={(e) => set('max_age', e.target.value)}
                />
                <Input
                  name="max_renewal_age"
                  label="Renewal age limit"
                  inputMode="numeric"
                  value={form.max_renewal_age}
                  error={err('max_renewal_age')}
                  onChange={(e) => set('max_renewal_age', e.target.value)}
                />
              </div>
              <Input
                name="waiting_period"
                label="Waiting period"
                value={form.waiting_period}
                onChange={(e) => set('waiting_period', e.target.value)}
                placeholder="e.g. 30 days, 2 years for PED"
              />
              <TextArea
                id="eligibility_notes"
                label="Eligibility notes"
                value={form.eligibility_notes}
                onChange={(v) => set('eligibility_notes', v)}
              />
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Where it is sold
                </h3>
                <p className={HELP_CLASS}>
                  No “allow” rule means sold everywhere. With “allow” rules, only those states and
                  PIN codes. “Exclude” always wins.
                </p>
                {err('service_areas') && <p className={ERROR_CLASS}>{err('service_areas')}</p>}
                <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <Select
                    label="Rule"
                    name="area_rule"
                    options={[
                      { value: 'allow', label: 'Allow' },
                      { value: 'exclude', label: 'Exclude' },
                    ]}
                    value={pinRule}
                    onChange={(e) => setPinRule(e.target.value as 'allow' | 'exclude')}
                  />
                  <div className="flex items-end gap-2">
                    <Select
                      label="State"
                      name="area_state"
                      options={[
                        { value: '', label: 'Choose a state' },
                        ...states.map((s) => ({ value: s.id, label: s.name })),
                      ]}
                      value={stateToAdd}
                      onChange={(e) => setStateToAdd(e.target.value)}
                    />
                    <Button size="sm" variant="outline" onClick={addState} disabled={!stateToAdd}>
                      Add
                    </Button>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="w-full">
                      <label htmlFor="pin-paste" className={LABEL_CLASS}>
                        PIN codes (paste up to 500)
                      </label>
                      <textarea
                        id="pin-paste"
                        rows={1}
                        value={pinPaste}
                        onChange={(e) => setPinPaste(e.target.value)}
                        className={FIELD_CLASS}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={addPins}
                      disabled={!pinPaste.trim()}
                    >
                      Add
                    </Button>
                  </div>
                </div>
                {areas.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {areas.map((a, i) => (
                      <span
                        key={`${a.rule_type}-${a.state ?? a.pincode}-${i}`}
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${
                          a.rule_type === 'exclude'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        }`}
                      >
                        {a.rule_type === 'exclude' ? 'Exclude' : 'Allow'}:{' '}
                        {a.state_name || states.find((s) => s.id === a.state)?.name || a.pincode}
                        <button
                          type="button"
                          aria-label="Remove rule"
                          onClick={() => setAreas(areas.filter((_, j) => j !== i))}
                          className="ml-1 font-bold"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'documents' && (
            <div className="space-y-4">
              <TextArea
                id="underwriting_guidelines"
                label="Underwriting guidelines"
                rows={5}
                value={form.underwriting_guidelines}
                error={err('underwriting_guidelines')}
                onChange={(v) => set('underwriting_guidelines', v)}
                help="Internal to your company. Not shown to brokers."
              />
              <TextArea
                id="exclusions"
                label="Exclusions"
                value={form.exclusions}
                onChange={(v) => set('exclusions', v)}
              />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {FILE_FIELDS.map((field) => {
                  const labels: Record<FileField, string> = {
                    brochure_pdf: 'Brochure (PDF)',
                    policy_wording_pdf: 'Policy wording (PDF)',
                    claim_form_pdf: 'Claim form (PDF)',
                    sample_policy_pdf: 'Sample policy (PDF)',
                  };
                  const stored = fileName(product?.[field]);
                  const change = files[field];
                  return (
                    <div key={field}>
                      <label htmlFor={field} className={LABEL_CLASS}>
                        {labels[field]}
                      </label>
                      <input
                        id={field}
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setFiles({ ...files, [field]: file });
                        }}
                        className="block w-full text-sm text-gray-700 dark:text-gray-300"
                      />
                      <p className={HELP_CLASS}>
                        {change instanceof File
                          ? `New: ${change.name}`
                          : change === ''
                            ? 'Will be removed on save'
                            : stored
                              ? `Current: ${stored}`
                              : 'None uploaded. PDF, at most 10 MB.'}
                      </p>
                      {stored && change === undefined && (
                        <button
                          type="button"
                          onClick={() => setFiles({ ...files, [field]: '' })}
                          className="text-xs text-red-600 hover:underline dark:text-red-400"
                        >
                          Remove
                        </button>
                      )}
                      {err(field) && <p className={ERROR_CLASS}>{err(field)}</p>}
                    </div>
                  );
                })}
                <Input
                  name="wording_version"
                  label="Wording version"
                  value={form.wording_version}
                  onChange={(e) => set('wording_version', e.target.value)}
                  placeholder="e.g. v3"
                />
                <Input
                  name="wording_effective_date"
                  label="Wording effective date"
                  type="date"
                  value={form.wording_effective_date}
                  onChange={(e) => set('wording_effective_date', e.target.value)}
                />
                <Input
                  name="product_page_url"
                  label="Product page link"
                  value={form.product_page_url}
                  error={err('product_page_url')}
                  onChange={(e) => set('product_page_url', e.target.value)}
                />
                <Input
                  name="claim_process_url"
                  label="Claim process link"
                  value={form.claim_process_url}
                  error={err('claim_process_url')}
                  onChange={(e) => set('claim_process_url', e.target.value)}
                />
                <Input
                  name="terms_url"
                  label="Terms link"
                  value={form.terms_url}
                  error={err('terms_url')}
                  onChange={(e) => set('terms_url', e.target.value)}
                />
              </div>
            </div>
          )}

          {tab === 'settings' && (
            <div className="space-y-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.is_visible_to_all}
                  onChange={(e) => set('is_visible_to_all', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                />
                Visible to all connected brokers
              </label>
              <Input
                name="tags"
                label="Tags"
                value={form.tags}
                onChange={(e) => set('tags', e.target.value)}
                helperText="Comma separated."
              />
              <p className={HELP_CLASS}>
                Switch the product off for one broker from the product&apos;s details (Products
                list, then Broker Access).
              </p>
            </div>
          )}
        </fieldset>

        {tab === 'history' && product && (
          <ProductHistory
            productId={product.id}
            canEdit={canChangeStatus}
            onReverted={async () => {
              const fresh = await productQuery.refetch();
              if (fresh.data) loadFrom(fresh.data);
            }}
          />
        )}

        {tab !== 'history' && (
          <div className="mt-6 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button variant="outline" onClick={() => navigate('/insurance/policies')}>
              Cancel
            </Button>
            {canEdit && (
              <Button
                onClick={onSave}
                isLoading={saveMutation.isPending}
                disabled={!dirty || stale}
              >
                {isEdit ? 'Save' : 'Create draft'}
              </Button>
            )}
          </div>
        )}
      </Card>

      <Modal
        isOpen={!!confirmRows}
        onClose={() => setConfirmRows(null)}
        title="Confirm live changes"
      >
        <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
          This goes live for brokers immediately.
        </p>
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <th className={TH_CLASS}>Field</th>
              <th className={TH_CLASS}>Was</th>
              <th className={TH_CLASS}>Will be</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {(confirmRows ?? []).map((row) => (
              <tr key={row.field}>
                <td className="px-2 py-1 text-gray-900 dark:text-gray-100">{row.label}</td>
                <td className="px-2 py-1 text-gray-700 dark:text-gray-300">{row.from}</td>
                <td className="px-2 py-1 font-medium text-gray-900 dark:text-gray-100">{row.to}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <ModalFooter>
          <Button variant="outline" onClick={() => setConfirmRows(null)}>
            Keep editing
          </Button>
          <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending}>
            Save and go live
          </Button>
        </ModalFooter>
      </Modal>

      {product && (
        <ProductStatusDialog
          productId={product.id}
          productName={product.name}
          target={statusTarget}
          onClose={() => setStatusTarget(null)}
        />
      )}
    </div>
  );
}
